import { RestError } from '@azure/core-http';
import {
  KnownResendNotificationChannel,
  KnownResendNotificationDtoNotificationType,
  OrderDto,
  ResendNotificationDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useImmer } from 'use-immer';
import { castDraft } from 'immer';
import { useUserByIdQuery } from '../../../../features/users/users.queries';
import { useMutationResendOrderNotification } from '../../../../features/orders/details/order-details.queries';
import { useMutationResendDeliveryNotification } from '../../../../features/deliveries/details/deliveries-details.queries';
import { UseMutationResult } from 'react-query';
import { Delivery } from '../../../../features/deliveries/models/delivery.model';

export interface ResendNotificationActionProps {
  delivery: OrderDto | Delivery;
  onAbort(): void;
  onSuccess(): void;
}

interface ResendNotificationActionState {
  isLoading: boolean;
  selectedChannel: KnownResendNotificationChannel.Email | KnownResendNotificationChannel.Sms | string;
  error?: RestError;
}

interface ResendNotificationActionHandlers {
  onSelectChannel(channel: ResendNotificationActionState['selectedChannel']): void;
  onSendNotification(): void;
  onCancel(): void;
}

interface ResendNotificationHookProps {
  projectID: string;
  userID?: string;
  deliveryID: string;
  userText: string;
  resendNotificationMutation: UseMutationResult<void, unknown, ResendNotificationDto>;
}

const initialState: ResendNotificationActionState = {
  isLoading: true,
  selectedChannel: KnownResendNotificationChannel.Sms
};

function useResendOrderNotification(delivery: OrderDto) {
  let args: ResendNotificationHookProps;
  const result = useMutationResendOrderNotification(delivery.retailer.id, delivery.id);
  args = {
    projectID: delivery.retailer.id,
    userID: delivery.customer?.id,
    deliveryID: delivery.id,
    userText: 'customer',
    resendNotificationMutation: result
  };
  return args;
}

function useResendDeliveryNotification(delivery: Delivery) {
  let args: ResendNotificationHookProps;
  const result = useMutationResendDeliveryNotification(delivery.id);
  args = {
    projectID: delivery.project.id,
    userID: delivery.deliveredTo?.party.id,
    deliveryID: delivery.id,
    userText: 'customer',
    resendNotificationMutation: result
  };
  return args;
}

function useResendWorkflowNotification(delivery: Delivery | OrderDto): ResendNotificationHookProps {
  let workflow;
  if ('retailer' in delivery) {
    workflow = useResendOrderNotification;
    return workflow(delivery);
  } else {
    workflow = useResendDeliveryNotification;
    return workflow(delivery);
  }
}

export function useResendNotification({ onAbort, onSuccess, delivery }: ResendNotificationActionProps) {
  const [state, setState] = useImmer<ResendNotificationActionState>(initialState);

  const args = useResendWorkflowNotification(delivery);

  const resendNotificationMutation = args.resendNotificationMutation;

  const userResult = useUserByIdQuery(args.projectID, args.userID, undefined, {
    onSuccess(recipient) {
      if (!recipient) {
        setState((draft) => {
          draft.isLoading = false;
          draft.error = castDraft(new RestError(`There was a problem fetching the ${args.userText} details`));
        });
      }
      setState((draft) => {
        draft.isLoading = false;
        if (recipient?.mobile) {
          draft.selectedChannel = KnownResendNotificationChannel.Sms;
        } else if (recipient?.email) {
          draft.selectedChannel = KnownResendNotificationChannel.Email;
        } else {
          draft.error = castDraft(
            new RestError(
              `We are unable to resend this notification as this ${args.userText} does not have contact details.`
            )
          );
        }
      });
    },
    onError(error) {
      const restError = castDraft(
        error instanceof RestError ? error : new RestError(`Unknown error fetching the ${args.userText} details`)
      );
      setState((draft) => {
        draft.isLoading = false;
        draft.error = restError;
      });
    }
  });

  const channelOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    if (userResult.isLoading) {
      return options;
    }
    if (userResult.data?.mobile) {
      options.push({
        label: `SMS: ${userResult.data.mobile}`,
        value: 'sms'
      });
    }
    if (userResult.data?.email) {
      options.push({
        label: `Email: ${userResult.data.email}`,
        value: 'email'
      });
    }
    return options;
  }, [userResult.data?.email, userResult.data?.mobile, userResult.isLoading]);

  const handlers: ResendNotificationActionHandlers = {
    onSelectChannel(channel) {
      setState((draft) => {
        draft.selectedChannel = channel;
      });
    },
    onSendNotification() {
      setState((draft) => {
        draft.isLoading = true;
      });
      resendNotificationMutation.mutate(
        {
          notificationType: KnownResendNotificationDtoNotificationType.Reminder,
          channels: [state.selectedChannel]
        },
        {
          onSuccess() {
            onSuccess();
          },
          onError(error) {
            const restError = castDraft(error instanceof RestError ? error : new RestError('Unknown error'));
            setState((draft) => {
              draft.isLoading = false;
              draft.error = restError;
            });
          }
        }
      );
    },
    onCancel() {
      onAbort();
    }
  };

  return {
    state,
    handlers,
    channelOptions
  };
}
